import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, BadgeCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { COUNTRIES } from '../data/constants';
import { StatCards, ListingsTab, FavoritesTab, MessagesTab } from '../components/dashboard/DashboardTabs';

const TAB_IDS = ['listings', 'favorites', 'messages'];

const Dashboard = () => {
  const { t } = useApp();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(TAB_IDS.includes(params.get('tab')) ? params.get('tab') : 'listings');

  const stats = useApi('/dashboard/stats');
  const mine = useApi('/listings', { mine: true, limit: 100 });
  const favs = useApi('/favorites/listings');
  const onChanged = () => { mine.reload(); stats.reload(); };
  const country = COUNTRIES.find((c) => c.code === user.country);

  const panels = {
    listings: <ListingsTab listings={mine.data} loading={mine.loading} onChanged={onChanged} />,
    favorites: <FavoritesTab listings={favs.data} loading={favs.loading} error={favs.error} reload={favs.reload} />,
    messages: <MessagesTab />,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#0F8B5C] flex items-center justify-center text-white font-bold text-lg">{user.avatar}</div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1A1A1A] flex items-center gap-2" data-testid="dashboard-welcome">
              {t('dash.welcome')}, {user.name.split(' ')[0]} {user.verified && <BadgeCheck className="w-5 h-5 text-[#0F8B5C]" />}
            </h1>
            <div className="flex items-center gap-2 text-sm text-[#7A7468]">
              <span className="px-2 py-0.5 rounded-full bg-[#F2B441]/20 text-[#8B6914] font-bold text-xs uppercase" data-testid="dashboard-plan">{user.plan}</span>
              <span>{country ? `${country.flag} ${country.name}` : user.country} · {t('dash.memberSince')} {new Date(user.createdAt).getFullYear()}</span>
            </div>
          </div>
        </div>
        <Link to="/post"><Button className="bg-[#0F8B5C] hover:bg-[#0A6E47] text-white" data-testid="dashboard-new-listing"><Plus className="w-4 h-4 mr-1.5" /> {t('dash.newListing')}</Button></Link>
      </div>

      <StatCards stats={stats.data} />

      <div className="bg-white rounded-2xl border border-[#EFEAE0] overflow-hidden">
        <div className="flex border-b border-[#EFEAE0] overflow-x-auto">
          {TAB_IDS.map((id) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`dashboard-tab-${id}`} className={`px-5 py-4 text-sm font-semibold transition-colors relative whitespace-nowrap ${tab === id ? 'text-[#0F8B5C]' : 'text-[#7A7468] hover:text-[#1A1A1A]'}`}>
              {t(`dash.${id}`)}
              {tab === id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0F8B5C]" />}
            </button>
          ))}
        </div>
        <div className="p-6">{panels[tab]}</div>
      </div>
    </div>
  );
};

export default Dashboard;
